import { isTrackingEnabled } from './tracking-toggles';

/**
 * Bridges a SMALL allowlist of conversion events into Vercel Web Analytics.
 *
 * Why an allowlist and not a passthrough: trackEvent() emits ~30 distinct
 * `vibe_*` names, including vibe_scroll, vibe_rage_click, vibe_toast_shown and
 * vibe_page_view. Web Analytics Plus bills $3 per 100k custom events, and the
 * high-frequency ones would dominate the bill while telling you nothing you
 * cannot already read in GA4. The Events panel is empty today (measured
 * 2026-08-25) because nothing calls track() at all; the fix for that is a few
 * meaningful events, not all of them.
 *
 * These names are the ones GA4 already treats as key events, so the two
 * systems stay comparable rather than measuring different things.
 */
export const VERCEL_CONVERSION_EVENTS: readonly string[] = [
  'vibe_generation_completed', // core activation: a playlist actually got made
  'vibe_open_in_spotify',      // value moment: the user left with the goods
  'vibe_sign_up',              // account created
  'vibe_lead',                 // waitlist / interest captured
];

const CONVERSION_SET = new Set(VERCEL_CONVERSION_EVENTS);

export function isVercelConversion(eventName: string): boolean {
  return CONVERSION_SET.has(eventName);
}

/**
 * Vercel accepts only flat string | number | boolean | null values. Anything
 * else (objects, arrays, undefined) is dropped rather than coerced, because a
 * silently stringified "[object Object]" column is worse than an absent one.
 */
export function sanitizeProps(
  input: Record<string, unknown> | undefined
): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  if (!input) return out;
  for (const [k, v] of Object.entries(input)) {
    if (v === null) { out[k] = null; continue; }
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      out[k] = v as string | number | boolean;
    }
    // undefined / object / array / function: intentionally dropped.
  }
  return out;
}

/**
 * Fire-and-forget. Dynamically imported so @vercel/analytics stays out of the
 * main chunk, matching how <Analytics /> itself is lazy-loaded in App.tsx.
 *
 * Every rejection is swallowed: this sits on the same call path as user
 * interactions (see the dataLayer.push comment in analytics.ts about a throwing
 * tag bubbling into a click handler), and telemetry must never break a click.
 */
export function sendVercelEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;
  if (!isVercelConversion(eventName)) return;
  if (!isTrackingEnabled('vercel-analytics')) return;

  void import('@vercel/analytics')
    .then(({ track }) => { track(eventName, sanitizeProps(params)); })
    .catch(() => { /* analytics is non-critical; never surface to the user */ });
}
